import { Component } from '@nicolas-riciotti/light2';

const INITIAL_VISIBLE_LIMIT = 20;
const LOAD_MORE_INCREMENT = 20;

function normalizeText(str = '') {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function levenshtein(a, b) {
  if (a === b) return 0;
  const alen = a.length, blen = b.length;
  if (!alen) return blen;
  if (!blen) return alen;

  const v = new Array(blen + 1);
  for (let j = 0; j <= blen; j++) v[j] = j;

  for (let i = 1; i <= alen; i++) {
    let prev = v[0];
    v[0] = i;
    const ai = a.charCodeAt(i - 1);

    for (let j = 1; j <= blen; j++) {
      const tmp = v[j];
      const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;

      v[j] = Math.min(
        v[j] + 1,
        v[j - 1] + 1,
        prev + cost
      );

      prev = tmp;
    }
  }

  return v[blen];
}

function maxEdits(len) {
  if (len <= 2) return 0;
  if (len <= 4) return 1;
  if (len <= 7) return 2;
  return 3;
}

function tokenMatchesWord(qTok, tTok) {
  if (!qTok) return true;
  if (tTok === qTok) return true;
  if (tTok.startsWith(qTok)) return true;

  const dist = levenshtein(qTok, tTok);

  if (qTok.length <= 4) return dist <= 1;

  if (qTok.length <= 7) {
    if (dist <= 1) return true;

    // allow missing repeated-letter cases like "satle" -> "seattle"
    const lengthDiff = Math.abs(qTok.length - tTok.length);
    return lengthDiff <= 2 && dist <= 2;
  }

  return dist <= 2;
}

function fuzzyMatchAllTokens(queryNorm, words) {
  if (!queryNorm) return true;

  const qWords = queryNorm.split(' ').filter(w => w.length >= 2);
  if (qWords.length === 0) return true;

  return qWords.every(qw => words.some(word => tokenMatchesWord(qw, word)));
}

export default class BlockStores extends Component {

  attached() {
    const $searchInput = this.querySelector('[data-search-input]');
    const $searchClear = this.querySelector('[data-search-clear]');
    const $slides = this.querySelectorAll('[data-slide]');
    const $loadMoreBtn = document.getElementById('load-more');

    const $noResultsMessage = document.getElementById('no-results-message');
    const $jobsContainer = document.getElementById('jobs-container');
    const $selectedFilters = this.querySelector('[data-selected-filters]');
    const $viewJobsButton = this.querySelector('[data-view-jobs-button]');

    let query = '';
    let visibleLimit = INITIAL_VISIBLE_LIMIT;

    const slides = [];

    $slides.forEach(el => {
      const title = el.getAttribute('data-title') || '';
      const address = el.getAttribute('data-address') || '';
      const city = el.getAttribute('data-city') || '';
      const state = el.getAttribute('data-state') || '';
      const zipCode = el.getAttribute('data-zip_code') || '';
      const country = el.getAttribute('data-country') || '';
      const website = el.getAttribute('data-website') || '';

      const searchTextNorm = normalizeText([
        title,
        address,
        city,
        state,
        zipCode,
        country,
        website
      ].join(' '));

      const searchWords = searchTextNorm.split(' ').filter(Boolean);

      slides.push({
        el,
        searchTextNorm,
        searchWords
      });
    });

    function resetQuery() {
      query = '';
      // The search box is not rendered at all for short store lists (<= 10).
      if ($searchInput) $searchInput.value = '';
      if ($searchClear) $searchClear.style.display = 'none';
    }

    function updateResultText(numResults) {
      if ($selectedFilters) {
        $selectedFilters.innerHTML = `<span class="t:mb-8fx">${numResults + ' result' + (numResults > 1 ? 's' : '')}</span>`;
      }

      if ($viewJobsButton) {
        $viewJobsButton.innerHTML = `View ${numResults} job${numResults > 1 ? 's' : ''}`;
      }
    }

    function applyVisibility() {
      let shown = 0;
      let totalVisible = 0;

      slides.forEach(slide => {
        if (slide.el.style.display === 'none') {
          slide.el.classList.add('hidden');
          return;
        }

        totalVisible++;

        if (shown < visibleLimit) {
          slide.el.classList.remove('hidden');
          shown++;
        } else {
          slide.el.classList.add('hidden');
        }
      });

      if ($loadMoreBtn) {
        $loadMoreBtn.style.display = visibleLimit >= totalVisible ? 'none' : '';
      }
    }

    function filterPosts() {
      const qNorm = normalizeText(query);
      let visibleCount = 0;

      slides.forEach(slide => {
        const matchQuery = fuzzyMatchAllTokens(qNorm, slide.searchWords);

        if (!matchQuery) {
          slide.el.style.display = 'none';
        } else {
          slide.el.style.removeProperty('display');
          visibleCount++;
        }
      });

      if (visibleCount === 0) {
        $noResultsMessage?.classList.remove('hidden');
        $noResultsMessage?.querySelectorAll('button')?.forEach(el => {
          el.removeAttribute('tabindex');
        });

        $jobsContainer?.classList.add('hidden');
      } else {
        $noResultsMessage?.classList.add('hidden');
        $noResultsMessage?.querySelectorAll('button')?.forEach(el => {
          el.setAttribute('tabindex', -1);
        });

        $jobsContainer?.classList.remove('hidden');
      }

      applyVisibility();
      updateResultText(visibleCount);
    }

    function filter() {
      visibleLimit = INITIAL_VISIBLE_LIMIT;
      filterPosts();
    }

    $searchClear?.addEventListener('click', () => {
      resetQuery();
      filter();
    });

    $searchInput?.addEventListener('input', () => {
      query = $searchInput.value;
      $searchClear.style.display = query === '' ? 'none' : 'block';
      filter();
    });

    $searchInput?.addEventListener('keydown', e => {
      if (e.isComposing) return;

      const isEnter = e.key === 'Enter' || e.keyCode === 13;
      if (!isEnter) return;

      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.blur();
    });

    $loadMoreBtn?.addEventListener('click', () => {
      visibleLimit += LOAD_MORE_INCREMENT;
      applyVisibility();
    });

    filterPosts();
  }
}

customElements.define('block-stores', BlockStores);